"use client"
import { motion } from "framer-motion"
import { Link } from "react-router-dom"
import { Rocket, CheckCircle, Clock, ArrowRight } from "lucide-react"
import Logo from "../components/Logo"

const fadeIn = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 },
}

const stagger = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
}

const Home = () => {
  return (
    <div className="container mx-auto px-4 py-8">
      <motion.div {...fadeIn} className="mb-12">
        <Logo />
      </motion.div>

      <motion.div {...fadeIn} className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">Welcome to Gennie Tools</h1>
        <p className="text-xl text-gray-600 dark:text-gray-300">Your Ultimate Free Online Tool Platform</p>
      </motion.div>

      <motion.div {...fadeIn} className="mb-12">
        <p className="text-lg text-gray-700 dark:text-gray-200 mb-4">
          Gennie Tools is an all-in-one platform offering a variety of free tools powered by AI and API integrations.
          Whether you're looking to download videos, find song lyrics, generate memes, or convert audio to text, we've
          got you covered!
        </p>
      </motion.div>

      <motion.section {...stagger} className="mb-12">
        <h2 className="text-3xl font-semibold text-gray-900 dark:text-white mb-6 flex items-center">
          <Rocket className="mr-2" /> What You Can Do with Gennie Tools
        </h2>
        <div className="grid md:grid-cols-2 gap-8">
          <motion.div {...fadeIn} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <CheckCircle className="mr-2 text-green-500" /> Active Services
            </h3>
            <ul className="space-y-2 text-gray-700 dark:text-gray-200">
              {[
                "Lyrics Finder – Enter the artist and song name to get the lyrics instantly.",
                "Video Downloader – Download videos from Facebook, Instagram, YouTube, and TikTok effortlessly.",
                "AI Programming Memes Generator – Get fun and intelligent programming-related memes.",
                "AI Audio to Text Converter – Convert any speech or audio into text with high accuracy.",
                "AI Quotes Generator – Generate inspiring and meaningful quotes instantly.",
                "AI Skin Beauty Enhancer – Enhance your skin appearance in images with AI magic.",
              ].map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start"
                >
                  <ArrowRight className="mr-2 mt-1 flex-shrink-0 text-indigo-500" />
                  <span>{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
          <motion.div {...fadeIn} className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-6">
            <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center">
              <Clock className="mr-2 text-blue-500" /> Coming Soon
            </h3>
            <ul className="space-y-2 text-gray-700 dark:text-gray-200">
              {[
                "PDF to Word Converter – Convert your PDFs into editable Word documents.",
                "Social Media SEO Improvement – Optimize your posts for better reach and engagement.",
                "Custom Thank You Email Generator – Generate professional thank-you emails effortlessly.",
                "Crypto Tools – Explore cryptocurrency-related services.",
              ].map((item, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="flex items-start"
                >
                  <ArrowRight className="mr-2 mt-1 flex-shrink-0 text-indigo-500" />
                  <span>{item}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>
        </div>
      </motion.section>

      <motion.section {...fadeIn} className="mb-12">
        <h2 className="text-3xl font-semibold text-gray-900 dark:text-white mb-6">How to Use Gennie Tools</h2>
        <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-200">
          {[
            "Navigate to the Sidebar or Drawer – All available tools are listed here.",
            "Select a Tool – Click on the service you need.",
            "Enter Your Input – Provide necessary details (e.g., song name, video URL, text, etc.).",
            "Click the Generate or Download Button – Get instant results!",
          ].map((item, index) => (
            <motion.li
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1 }}
            >
              {item}
            </motion.li>
          ))}
        </ol>
      </motion.section>

      <motion.section {...fadeIn} className="mb-12">
        <h2 className="text-3xl font-semibold text-gray-900 dark:text-white mb-6">Why Choose Gennie Tools?</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {["100% Free to Use", "Fast & Reliable Performance", "AI-Powered Features", "No Sign-Up Required"].map(
            (item, index) => (
              <motion.div
                key={index}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-lg p-4 text-center"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <CheckCircle className="mx-auto mb-2 text-green-500" />
                <p className="text-gray-700 dark:text-gray-200">{item}</p>
              </motion.div>
            ),
          )}
        </div>
      </motion.section>

      <motion.div {...fadeIn} className="text-center">
        <Link
          to="/lyrics-finder"
          className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg hover:bg-indigo-700 transition-colors duration-300 transform hover:scale-105"
        >
          Try it now!
        </Link>
      </motion.div>
    </div>
  )
}

export default Home

